<template>
    <analytics-sidebar-wrapper @reset="emit('reset')">
        <div class="col-span-4 lg:col-span-4">
            <div class="bg-slate-100 border border-white rounded-lg rounded-b-none border-b-0">
                <analytics-sidebar-title @reset="handleDateRangeOption('last_30_days')">
                    Range
                </analytics-sidebar-title>

                <div class="p-3 flex flex-col gap-3">
                    <div
                        v-for="rangeOption in rangeOptions"
                        :key="rangeOption.value"
                        class="flex gap-2 items-center text-xs cursor-pointer"
                        :data-test="`range-${rangeOption.value}`"
                        tabindex="0"
                        @click.prevent="handleDateRangeOption(rangeOption.value)"
                        @keydown.space.prevent="handleDateRangeOption(rangeOption.value)"
                        @keydown.enter.prevent="handleDateRangeOption(rangeOption.value)"
                    >
                        <vx-radio :modelValue="selectedRange.value === rangeOption.value"></vx-radio>
                        {{ rangeOption.label }}
                    </div>
                </div>
            </div>

            <transition
                enter-from-class="opacity-0 -translate-y-4"
                enter-to-class="opacity-100 translate-y-0"
                leave-from-class="opacity-100 translate-y-0"
                leave-to-class="opacity-0 -translate-y-4"
            >
                <div v-if="isDatePickerOpened" class="absolute z-20 mb-8 w-full transition lg:max-w-md" @click.stop>
                    <vx-date-picker
                        mode="date"
                        :modelValue="defaultDatePickerRange"
                        @update:modelValue="handleDatePickerRange"
                        is-range
                        v-on-click-outside="() => closeDatePickerRange()"
                        :timezone="getClientTz()"
                        :maxDate="maxCustomDate"
                        :minDate="minCustomDate"
                        @dayclick="handleDayClick"
                    />
                </div>
            </transition>

            <div
                class="flex text-xs text-black rounded-b-2xl border border-solid shadow border-white border-t-slate-300/70"
            >
                <div class="flex grow items-center justify-between">
                    <button
                        @click.prevent="isPrevRangeAllowed && setPrevRange()"
                        data-test="prev-btn"
                        :disabled="!isPrevRangeAllowed"
                        class="cursor-pointer font-sans p-2 grow flex pl-4 py-3 rounded-b-2xl bg-slate-200 flex-row items-center gap-2 font-semibold transition"
                        :class="{
                            'hover:bg-slate-300': isPrevRangeAllowed,
                            'cursor-default': !isPrevRangeAllowed,
                        }"
                    >
                        <font-awesome-icon widthAuto :icon="faArrowLeft"></font-awesome-icon>
                        {{ selectedRange.value === 'selected_custom' ? 'Prev' : 'Previous Period' }}
                    </button>
                    <button
                        v-if="selectedRange.value === 'selected_custom'"
                        @click.prevent="isNextRangeAllowed && setNextRange()"
                        data-test="next-btn"
                        :disabled="!isNextRangeAllowed"
                        class="cursor-pointer font-sans justify-end p-2 grow flex pr-4 py-3 rounded-b-2xl bg-slate-200 flex-row items-center gap-2 font-semibold transition"
                        :class="{
                            'hover:bg-slate-300': isNextRangeAllowed,
                            'cursor-default': !isNextRangeAllowed,
                        }"
                    >
                        Next
                        <font-awesome-icon widthAuto :icon="faArrowRight"></font-awesome-icon>
                    </button>
                </div>
            </div>
        </div>

        <div v-if="FEATURES.contact_groups" class="col-span-4 lg:col-span-4">
            <analytics-sidebar-checkboxes
                v-model="fetchParams.filters.sender_types"
                :disabled="!isSenderChangeAllowed"
                :options="SENDER_TYPES"
            >
                <template v-slot:title> Sender Type </template>
            </analytics-sidebar-checkboxes>
        </div>

        <div class="col-span-4 lg:col-span-4">
            <analytics-sidebar-checkboxes
                v-model="fetchParams.filters.audience_types"
                :options="audienceTypesOptionsList"
            >
                <template v-slot:title> Audience Type </template>
            </analytics-sidebar-checkboxes>
        </div>

        <div class="col-span-4 lg:col-span-5">
            <analytics-sidebar-checkboxes v-model="fetchParams.filters.resource_types" :options="resourceTypesOptions">
                <template v-slot:title> Resource Type </template>
            </analytics-sidebar-checkboxes>
        </div>

        <div v-if="!props.hideCategoryFilter" class="col-span-12">
            <analytics-sidebar-category v-model="resourceCategory" />
        </div>

        <div v-if="!props.hideResourceFilter" data-test="resources-filter" class="col-span-12">
            <analytics-sidebar-box @reset="fetchParams.filters.resources = undefined">
                <template v-slot:title> Resources </template>

                <div
                    v-if="selectedResources.length"
                    @click="handleResourceSelection"
                    class="group/selection cursor-pointer select-none relative text-xs flex flex-col py-4 px-3 gap-2"
                >
                    <div
                        v-for="resource in selectedResources"
                        :key="`select_${resource.resource_type}_${resource.resource_id}`"
                        class="flex items-baseline justify-start gap-2 pr-1 transition duration-300 group-hover/selection:opacity-5 group-hover/selection:blur-[1px]"
                    >
                        <div class="min-w-0 grow break-words">
                            {{ resource.resource_name || resourceName(resource) || 'Loading...' }}
                        </div>
                        <font-awesome-icon
                            widthAuto
                            :icon="resourceTypeIcon(resource.resource_type, 'regular')"
                            class="translate-y-0.5 text-sm"
                        ></font-awesome-icon>
                    </div>

                    <div
                        class="absolute inset-0 flex flex-wrap items-center justify-center gap-2 overflow-hidden text-xs font-bold opacity-0 transition group-hover/selection:opacity-100 group-hover/selection:delay-75"
                    >
                        <div
                            class="absolute flex items-center justify-center gap-2 transition duration-300"
                            :class="[selectingResources ? 'opacity-100' : 'translate-y-12 opacity-0']"
                        >
                            Done
                            <font-awesome-icon widthAuto :icon="faCircleCheck"></font-awesome-icon>
                        </div>
                        <div
                            class="absolute flex items-center justify-center gap-2 transition duration-300"
                            :class="[!selectingResources ? 'opacity-100' : '-translate-y-12 opacity-0']"
                        >
                            Manage Selection
                            <font-awesome-icon widthAuto :icon="faPencil"></font-awesome-icon>
                        </div>
                    </div>
                </div>
                <button
                    @click="handleResourceSelection"
                    data-test="handle-resource-selection"
                    class="group/add-resources cursor-pointer -mt-3 -mx-3 -mb-3 flex w-[calc(100%_+_24px)] py-3 rounded-b-lg justify-center relative overflow-hidden items-center text-xs text-black bg-slate-200 border border-solid border-b-0 border-x-0 border-t-slate-300 font-semibold transition hover:bg-slate-300"
                >
                    <div
                        class="flex items-center gap-2 transition duration-300"
                        :class="{ 'group-hover/add-resources:-translate-y-12': selectingResources }"
                    >
                        Add Resources
                        <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                    </div>
                    <div
                        class="absolute inset-0 flex items-center justify-center gap-2 transition duration-300 translate-y-12"
                        :class="{ 'group-hover/add-resources:translate-y-0': selectingResources }"
                    >
                        Done
                        <font-awesome-icon widthAuto :icon="faCircleCheck"></font-awesome-icon>
                    </div>
                </button>
            </analytics-sidebar-box>
        </div>
    </analytics-sidebar-wrapper>
</template>

<script setup>
import { VxDatePicker, VxRadio } from '@voxie/frontend-components';
import { vOnClickOutside } from '@vueuse/components';
import { computed, inject, nextTick, ref } from 'vue';
import {
    getDateRange,
    formatDateForRange,
    getCurrentDate,
    getMinAllowedDate,
    resourceTypesOptions,
    resourceTypeIcon,
} from '~/utils/analytics';
import { getClientTz, shortDate } from '~/utils/date';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faArrowLeft, faArrowRight, faCircleCheck, faCirclePlus, faPencil } from '@fortawesome/pro-solid-svg-icons';
import { audienceTypes as audienceTypesOptions } from '~/constants/campaign';
import { SENDER_TYPES } from '~/constants/senderTypes';
import { useAnalyticsSelectResources } from '~/components/analytics/partial/select-resources/useAnalyticsSelectResources';
import AnalyticsSidebarWrapper from './partial/sidebar/AnalyticsSidebarWrapper.vue';
import AnalyticsSidebarCheckboxes from './partial/sidebar/AnalyticsSidebarCheckboxes.vue';
import AnalyticsSidebarCategory from './partial/sidebar/AnalyticsSidebarCategory.vue';
import AnalyticsSidebarTitle from './partial/sidebar/AnalyticsSidebarTitle.vue';
import AnalyticsSidebarBox from './partial/sidebar/AnalyticsSidebarBox.vue';

const FEATURES = inject('FEATURES');

const props = defineProps({
    orgUnitId: {
        type: Number,
    },
    groupId: {
        type: String,
    },
    hideCategoryFilter: {
        type: Boolean,
        default: false,
    },
    hideResourceFilter: {
        type: Boolean,
        default: false,
    },
    minAllowedDate: {
        type: Object,
        required: true,
    },
});

const fetchParams = defineModel({
    type: Object,
    required: true,
});

const { selectingResources, selectedResources, resourceName } = useAnalyticsSelectResources();

const emit = defineEmits(['reset', 'applyResources']);

const minGroupAllowedDate = computed(() => getMinAllowedDate(true));

const resourceCategory = computed({
    set: (resourceCategory) => {
        fetchParams.value.filters.resource_categories = resourceCategory ? [resourceCategory] : undefined;
    },
    get: () => {
        return fetchParams.value.filters.resource_categories?.[0];
    },
});

const defaultRangeOptions = [
    { label: 'Last 7 Days', value: 'last_7_days', ...getDateRange('last_7_days') },
    { label: 'Last 14 Days', value: 'last_14_days', ...getDateRange('last_14_days') },
    { label: 'Last 30 Days', value: 'last_30_days', ...getDateRange('last_30_days') },
    { label: 'Current Month', value: 'month_today', ...getDateRange('month_today') },
];

const audienceTypesOptionsList = audienceTypesOptions().map((audienceType) => ({
    value: audienceType.name,
    label: audienceType.label,
}));

const isSenderChangeAllowed = computed(
    () =>
        FEATURES.contact_groups &&
        dayjs.utc(fetchParams.value.range[0]).tz(getClientTz()).isSameOrAfter(minGroupAllowedDate.value, 'day')
);

// dayjs calculates an end date starting from a next day. in order to get the right range including a current day we decrease max days by 1.
const MAX_DAYS_RANGE = 99;

const clickedDay = ref(null);

const handleDayClick = (event) => {
    if (!clickedDay.value) {
        clickedDay.value = event.date;
    }
};

const maxAllowed = computed(() => {
    const currentDate = getCurrentDate();

    // current day is only available if the time is at least 01:30 local.
    if (currentDate.isAfter(currentDate.clone().set('hour', 1).set('minutes', 30))) {
        return currentDate.toDate();
    }

    return currentDate.subtract(1, 'day').endOf('day').toDate();
});

const maxCustomDate = computed(() => {
    if (clickedDay.value && dayjs(clickedDay.value).add(MAX_DAYS_RANGE, 'days').isAfter(maxAllowed.value)) {
        return maxAllowed.value;
    }

    if (clickedDay.value) {
        return dayjs(clickedDay.value).add(MAX_DAYS_RANGE, 'days').toDate();
    }

    return maxAllowed.value;
});

const minCustomDate = computed(() => {
    if (clickedDay.value && dayjs(clickedDay.value).subtract(MAX_DAYS_RANGE, 'days').isBefore(props.minAllowedDate)) {
        return props.minAllowedDate.toDate();
    }

    if (clickedDay.value) {
        return dayjs(clickedDay.value).subtract(MAX_DAYS_RANGE, 'days').toDate();
    }

    return props.minAllowedDate.toDate();
});

const selectedRange = computed(() => {
    const start = dayjs.utc(fetchParams.value.range[0]).tz(getClientTz());
    const end = dayjs.utc(fetchParams.value.range[1]).tz(getClientTz());

    const option = defaultRangeOptions
        .filter((option) => option.start_date && option.end_date)
        .find(
            (option) =>
                dayjs.utc(option.start_date).isSame(start, 'day') && dayjs.utc(option.end_date).isSame(end, 'day')
        );

    return (
        option || {
            label: `${shortDate(start)} - ${shortDate(
                end.clone().isSame(getCurrentDate(), 'hour') ? end : end.subtract(1, 'day')
            )}`,
            value: 'selected_custom',
        }
    );
});

const customRangeOption = computed(() => {
    if (selectedRange.value?.value === 'selected_custom') {
        return selectedRange.value;
    }
    return {
        label: 'Custom',
        value: 'custom',
    };
});

const rangeOptions = computed(() => {
    return defaultRangeOptions.concat([{ ...customRangeOption.value }]);
});

// necessary for rendering and selecting the current hour and minute with datepicker
const defaultDatePickerRange = computed(() => {
    const selectedEndDate = dayjs.utc(fetchParams.value.range[1]);
    const endDate = dayjs.min([dayjs(maxCustomDate.value).tz(getClientTz()).startOf('day'), selectedEndDate]);

    return {
        start: dayjs.utc(fetchParams.value.range[0]).toDate(),
        // on setRange we add 1 day, here we subtract it to display the correct information in the ui
        end: endDate.isSame(selectedEndDate) ? selectedEndDate.subtract(1, 'day').toDate() : endDate.toDate(),
    };
});

const isDatePickerOpened = ref(false);

const handleDateRangeOption = (optionKey) => {
    if (optionKey === 'custom' || optionKey === 'selected_custom') {
        isDatePickerOpened.value = true;
        return;
    }

    const option = rangeOptions.value.find((o) => o.value === optionKey);

    if (option !== undefined && selectedRange.value.value !== optionKey) {
        fetchParams.value.range = [option.start_date, option.end_date];
    }
};

const getInterval = () => {
    const startDate = dayjs(fetchParams.value.range[0]).tz(getClientTz());
    const endDate = dayjs(fetchParams.value.range[1]).tz(getClientTz()).add(1, 'hour');
    const interval = endDate.diff(startDate, 'days');

    return endDate.clone().subtract(1, 'hour').isSame(getCurrentDate(), 'hour') ? interval + 1 : interval;
};

const prevRange = computed(() => {
    const interval = getInterval();

    // end becomes start date
    const end = dayjs(fetchParams.value.range[0]).tz(getClientTz()).subtract(1, 'day').startOf('day');
    const start = end.clone().subtract(interval - 1, 'day');

    return { start, end };
});

const isPrevRangeAllowed = computed(() => {
    return prevRange.value.start.isSameOrAfter(props.minAllowedDate, 'day');
});

const setPrevRange = () => {
    setRange(prevRange.value.start, prevRange.value.end);
};

const nextRange = computed(() => {
    const interval = getInterval();

    // start becomes end date
    const start = dayjs.utc(fetchParams.value.range[1]).tz(getClientTz()).startOf('day');
    const end = start.clone().add(interval - 1, 'day');

    return { start, end };
});

const setNextRange = () => {
    setRange(nextRange.value.start, nextRange.value.end);
};

const isNextRangeAllowed = computed(() => {
    if (dayjs.utc(fetchParams.value.range[1]).tz(getClientTz()).isSameOrAfter(getCurrentDate(), 'hour')) {
        return false;
    }

    return !nextRange.value.end.isAfter(maxAllowed.value);
});

/**
 * @param {dayjs.Dayjs} start
 * @param {dayjs.Dayjs} end
 */
const setRange = (start, end) => {
    fetchParams.value.range = [
        formatDateForRange(start),
        formatDateForRange(dayjs.min([getCurrentDate(), end.add(1, 'day').startOf('day')])),
    ];
};

const handleDatePickerRange = async ({ start, end }) => {
    setRange(dayjs.utc(start).tz(getClientTz()), dayjs.utc(end).tz(getClientTz()));
    isDatePickerOpened.value = false;
    await nextTick();
    clickedDay.value = null;
};

const closeDatePickerRange = () => {
    isDatePickerOpened.value = false;
};

const handleResourceSelection = () => {
    if (selectingResources.value) {
        emit('applyResources');
    } else {
        selectingResources.value = true;
    }
};
</script>
